import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/Layout";
import { Loading } from "@/components/common/States";
import CandidateList from "@/components/builder/CandidateList";
import BuildSummary from "@/components/builder/BuildSummary";
import SEO from "@/components/common/SEO";

const KEY = "cfpv_build";
const RING = { incompatible: "border-red-500 text-red-700", possible: "border-yellow-400 text-yellow-700", verified: "border-emerald-400 text-emerald-700" };

function SavedBuilds({ onLoad, current, selections }) {
  const qc = useQueryClient();
  const [name, setName] = useState("Yeni drone");
  const { data = [] } = useQuery({ queryKey: ["builds"], queryFn: async () => (await api.get("/builds/mine")).data });
  const save = async () => {
    try {
      const body = { name, selections: Object.fromEntries(Object.entries(selections).map(([k, v]) => [k, { type: v.type, id: v.id }])) };
      const { data: b } = current ? await api.put(`/builds/${current}`, body) : await api.post("/builds", body);
      onLoad(b, false);
      toast.success("Toplama kaydedildi");
      qc.invalidateQueries({ queryKey: ["builds"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel space-y-3 p-4" data-testid="saved-builds">
      <div className="flex gap-2"><Input data-testid="build-name-input" value={name} onChange={(e) => setName(e.target.value)} /><Button onClick={save} data-testid="save-build-button" className="bg-[#0071e3] text-white">Kaydet</Button></div>
      {data.length > 0 && <div className="flex flex-wrap gap-2">{data.map((b) => <button key={b.id} onClick={() => { setName(b.name); onLoad(b, true); }} data-testid={`load-build-${b.id}`} className="rounded border border-black/[0.08] px-2 py-1 text-xs hover:border-[#0071e3]">{b.name}</button>)}</div>}
    </div>
  );
}

export default function Builder() {
  const { user } = useAuth();
  const { data: meta } = useQuery({ queryKey: ["builder-steps"], queryFn: async () => (await api.get("/builder/steps")).data });
  const [state, setState] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)) || { selections: {}, step: "esc" }; } catch { return { selections: {}, step: "esc" }; } });
  const [hide, setHide] = useState(false);
  const [buildId, setBuildId] = useState(null);
  const { selections, step } = state;
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  const refs = Object.fromEntries(Object.entries(selections).map(([k, v]) => [k, { type: v.type, id: v.id }]));
  const selKey = JSON.stringify(refs);

  const { data: cand, isLoading } = useQuery({ queryKey: ["candidates", step, selKey], queryFn: async () => (await api.post("/builder/candidates", { step, selections: refs })).data });
  const { data: evaluation } = useQuery({ queryKey: ["evaluate", selKey], queryFn: async () => (await api.post("/builder/evaluate", { selections: refs })).data });
  const statusOf = (s) => evaluation?.parts?.find((p) => p.step === s)?.status;

  if (!meta) return <Loading />;
  const steps = meta.steps;
  const idx = steps.findIndex((s) => s.key === step);
  const select = (c) => {
    const next = { ...selections, [step]: { type: c.type, id: c.id, title: c.title } };
    setState({ selections: next, step: steps[Math.min(idx + 1, steps.length - 1)].key });
  };
  const load = (b, replace) => {
    setBuildId(b.id);
    if (replace) setState({ selections: b.selections, step: "esc" });
  };

  return (
    <Page>
      <p className="eyebrow">BU SAYFA BETA SÜRÜMÜNDEDÜR ŞUAN GELİŞTİRMEYE DEVAM EDİLMEKTEDİR!!!</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold">Uyumlu parçalarla drone topla</h1>
      <p className="mt-2 max-w-2xl text-sm text-zinc-500">Her adımda önceki seçimleriniz teknik verilere göre kontrol edilir. Katalogda eksik veri varsa sonuç "doğrulanmış" olarak gösterilmez.</p>
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="builder-stepper">
        {steps.map((s, i) => {
          const st = statusOf(s.key);
          return (
            <button key={s.key} onClick={() => setState({ ...state, step: s.key })} data-testid={`builder-step-${s.key}`}
              className={`shrink-0 rounded-md border px-3 py-2 text-left text-xs ${s.key === step ? "border-[#0071e3] bg-[#0071e3]/10 shadow-[0_0_12px_rgba(0,113,227,0.25)]" : selections[s.key] ? RING[st] || "border-black/[0.08]" : "border-black/[0.08] text-zinc-500"}`}>
              <span className="font-mono">{i + 1}.</span> {s.label}
              {selections[s.key] && <span className="block max-w-[140px] truncate text-[10px] opacity-80">{selections[s.key].title}</span>}
            </button>
          );
        })}
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl font-semibold">{idx + 1}. {steps[idx].label} seçimi</h2>
            <div className="flex items-center gap-3 text-sm">
              <label className="flex items-center gap-2 text-zinc-500"><Switch checked={hide} onCheckedChange={setHide} data-testid="hide-incompatible-switch" />Uyumsuzları gizle</label>
              {selections[step] && <Button size="sm" variant="outline" className="bg-transparent" data-testid="clear-step-button" onClick={() => { const n = { ...selections }; delete n[step]; setState({ ...state, selections: n }); }}>Seçimi kaldır</Button>}
            </div>
          </div>
          {isLoading ? <Loading /> : <CandidateList items={cand?.items || []} selectedId={selections[step]?.id} onSelect={select} hideIncompatible={hide} />}
        </section>
        <aside className="space-y-4">
          <div className="flex items-center justify-between"><h2 className="font-display text-xl font-semibold">Sistem özeti</h2>
            <Button size="sm" variant="ghost" data-testid="reset-build-button" onClick={() => { setBuildId(null); setState({ selections: {}, step: "esc" }); }}>Sıfırla</Button></div>
          {user && <SavedBuilds onLoad={load} current={buildId} selections={selections} />}
          <BuildSummary evaluation={evaluation} onGoto={(s) => setState({ ...state, step: s })} />
        </aside>
      </div>
    </Page>
  );
}
